import { motion } from 'framer-motion';
import { DocumentTextIcon, LinkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';

const ContentPage = ({ user }) => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Content Management
        </h1>
        <p className="text-lg text-gray-600">
          Upload files, add URLs, and manage your content library
        </p>
      </motion.div>

      {/* Content Upload Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* File Upload */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card"
        >
          <div className="text-center">
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-primary-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Upload Files
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Upload PDFs, audio files, or documents for AI processing
            </p>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary-500 transition-colors cursor-pointer">
              <div className="text-center">
                <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  <span className="font-medium text-primary-600">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  PDF, MP3, WAV, M4A up to 100MB
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* URL Input */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="card"
        >
          <div className="text-center">
            <LinkIcon className="mx-auto h-12 w-12 text-green-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Add URLs
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Add YouTube, TikTok, Instagram, or website URLs
            </p>
            
            <div className="space-y-4">
              <input
                type="url"
                placeholder="https://youtube.com/watch?v=..."
                className="input-field"
              />
              <button className="w-full btn-primary">
                Add Content
              </button>
            </div>
            
            <div className="mt-4 text-xs text-gray-500">
              <p>Supported: YouTube, TikTok, Instagram, Websites</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Recent Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="card"
      >
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          Recent Content
        </h3>
        
        <div className="space-y-4">
          {[
            {
              id: 1,
              title: 'AI Tools Review Video',
              type: 'YouTube',
              url: 'https://youtube.com/watch?v=example',
              addedAt: '2 hours ago',
              status: 'processed',
            },
            {
              id: 2,
              title: 'Marketing Strategy PDF',
              type: 'Document',
              url: 'marketing-strategy.pdf',
              addedAt: '1 day ago',
              status: 'processing',
            },
            {
              id: 3,
              title: 'Competitor Analysis',
              type: 'Website',
              url: 'https://competitor.com/blog/post',
              addedAt: '2 days ago',
              status: 'processed',
            },
          ].map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {item.type} • Added {item.addedAt}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  item.status === 'processed' 
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {item.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Demo Notice */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="bg-green-50 border border-green-200 rounded-lg p-4"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <DocumentTextIcon className="h-5 w-5 text-green-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Demo Mode - Content Management
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <p>
                File uploads and URL processing are simulated in demo mode. In production, files will be stored in R2 and processed by AI.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ContentPage; 